// PAIRED WITH server/lib/docTypes.js PAYABLE_DOC_TYPES — the document types
// that are money owed to a supplier, and therefore can carry a payment. The
// server is the gate (POST /payments 400s for anything else); this mirror only
// decides what the client OFFERS, so a receipt or a credit note never gets a
// "שולם" checkbox whose payment the server would then refuse. Change one →
// change the other (docs/brain/07 sync pairs).
export const PAYABLE_DOC_TYPES = Object.freeze([
  'חשבונית',
  'חשבונית מרכזת',
  'תעודת משלוח',
  'חשבונית/קבלה',
  'חשבון עסקה',
]);
export const isPayableDocType = (t) => PAYABLE_DOC_TYPES.includes(t);

// PAIRED WITH server/lib/docTypes.js DUP_EXEMPT_DOC_TYPES — the types whose
// doc_num legitimately repeats (a כרטסת reprints the supplier's account number
// every month), and which are therefore not deduplicated on it. The server and
// its unique index are the gate; this mirror only suppresses the client's own
// pre-save "כבר קיים" toast, which would otherwise block a save the server
// accepts. Change one → change the other (docs/brain/07 sync pairs).
export const DUP_EXEMPT_DOC_TYPES = Object.freeze(['כרטסת']);
export const isDupExemptDocType = (t) => DUP_EXEMPT_DOC_TYPES.includes(t);

// PAIRED WITH server/lib/docTypes.js RECORDABLE_CREDIT_DOC_TYPES (owner,
// 2026-09-15). חשבונית זיכוי ALONE (not תעודת החזרה) is a NEGATIVE invoice:
// "בוצע תשלום" records it as paid (server/lib/amountOwed.js CREDIT_NOTE). Used
// wherever the client treats it differently from an invoice: its full value
// always nets off the total (SupplierDocumentsPage.jsx footer), it always
// settles in full (PaymentModal.jsx — no amount field), and it never offers
// month approval (paymentLifecycle.js approvalOfferForDoc).
export const CREDIT_NOTE_DOC_TYPES = Object.freeze(['חשבונית זיכוי']);
export const isCreditNoteDocType = (t) => CREDIT_NOTE_DOC_TYPES.includes(t);

// PAIRED WITH server/lib/docTypes.js isCreditNoteDoc (owner, 2026-09-24). Any
// invoice in minus — including a מרכזת, the only type that can link תעודות —
// behaves exactly like a חשבונית זיכוי. Keyed on the document because the sign
// decides; use this (not isCreditNoteDocType) wherever a document row is at hand.
const NEGATIVE_INVOICE_DOC_TYPES = ['חשבונית', 'חשבונית מרכזת', 'חשבונית/קבלה', 'חשבון עסקה'];
export const isCreditNoteDoc = (d) => !!d && (isCreditNoteDocType(d.doc_type)
  || (NEGATIVE_INVOICE_DOC_TYPES.includes(d.doc_type) && Number(d.total) < 0));

// PAIRED WITH server/lib/docTypes.js RECORDABLE_DOC_TYPES (PAYABLE_DOC_TYPES
// plus חשבונית זיכוי). Any client list that means "every document a payments
// row can exist against" — as opposed to "every invoice" (PAYABLE_DOC_TYPES
// above) — must use this one, or it disagrees with the server aggregates built
// on the same union (payments.js computeOverviewRows, the lifecycle counts).
export const RECORDABLE_DOC_TYPES = Object.freeze([...PAYABLE_DOC_TYPES, ...CREDIT_NOTE_DOC_TYPES]);

// PAIRED WITH server/lib/docTypes.js SUPPLIER_DOC_TYPES — everything in a
// supplier's MONEY picture, and therefore everything /supplier/:id lists (the
// server filters that page on the same list). The page's type filter used to
// carry its own copy, which offered קבלה (never listed there, so the filter
// always came back empty) and omitted חשבונית מרכזת (listed, so it could not
// be filtered for).
export const SUPPLIER_DOC_TYPES = Object.freeze([...PAYABLE_DOC_TYPES, 'חשבונית זיכוי', 'תעודת החזרה']);

// PAIRED WITH server/lib/noteLinkFilter.js UNLINKED_NOTE_KEYS (owner,
// 16/10/2026). The "- לא מקושרת" options of the /documents סוג מסמך filter:
// pseudo keys, never a stored doc_type. The server turns each into "this note
// type with no link to a חשבונית מרכזת" (dismissed and draft notes excluded);
// the client only offers them and labels them. Change one → change the other
// (docs/brain/07 sync pairs).
export const UNLINKED_NOTE_FILTER_LABELS = Object.freeze({
  'unlinked:תעודת משלוח': 'תעודת משלוח - לא מקושרת',
  'unlinked:תעודת החזרה': 'תעודת החזרה - לא מקושרת',
});
// What a doc-type filter value reads as on screen (option and chip): the
// pseudo keys get their label, real types are their own label.
export const docTypeFilterLabel = (key) =>
  (Object.hasOwn(UNLINKED_NOTE_FILTER_LABELS, key) ? UNLINKED_NOTE_FILTER_LABELS[key] : key);
